import { http } from '@/lib/http';
import type {
  CreateJobWorkOrderPayload,
  JobWorkChallan,
  JobWorkOrder,
  JobWorkOrderRow,
  JobWorkOrderStatus,
  ProductionRecipe,
  ProductionRecipeRow,
  ProductionRecipeStatus,
  ProductionStepMode,
  SaveRecipePayload,
} from '@/types/production';

const BASE = '/api/v1/production/recipes';
const JWO_BASE = '/api/v1/production/job-work-orders';

export interface RecipeListQuery {
  status?: ProductionRecipeStatus;
  targetItemId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface RecipeListResponse {
  items: ProductionRecipeRow[];
  total: number;
  page: number;
  pageSize: number;
}

export const productionService = {
  listRecipes: (query: RecipeListQuery = {}) =>
    http.get<RecipeListResponse>(BASE, { params: query }),
  getRecipe: (id: string) => http.get<ProductionRecipe>(`${BASE}/${id}`),
  createRecipe: (payload: { targetItemId: string; name: string; notes?: string }) =>
    http.post<ProductionRecipe, typeof payload>(BASE, payload),
  saveRecipe: (id: string, payload: SaveRecipePayload) =>
    http.put<ProductionRecipe, SaveRecipePayload>(`${BASE}/${id}`, payload),
  activateRecipe: (id: string) => http.patch<ProductionRecipe, undefined>(`${BASE}/${id}/activate`),
  archiveRecipe: (id: string) => http.patch<ProductionRecipe, undefined>(`${BASE}/${id}/archive`),
  deleteRecipe: (id: string) => http.delete<{ deleted: boolean }>(`${BASE}/${id}`),
};

export interface JwoListQuery {
  status?: JobWorkOrderStatus;
  mode?: ProductionStepMode;
  vendorId?: string;
  outputItemId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface JwoListResponse {
  items: JobWorkOrderRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface JwoIssuePayload {
  challanNumber?: string;
  expectedReturnAt?: string;
  ewayBillNo?: string;
  ewayBillDate?: string;
  vehicleNo?: string;
  servicePoNo?: string;
}

export interface JwoReceivePayload {
  actualOutputQty?: number;
  scrapQty?: number;
  returnEwayBillNo?: string;
  returnEwayBillDate?: string;
  returnVehicleNo?: string;
}

export const jobWorkOrdersService = {
  list: (query: JwoListQuery = {}) => http.get<JwoListResponse>(JWO_BASE, { params: query }),
  get: (id: string) => http.get<JobWorkOrder>(`${JWO_BASE}/${id}`),
  getChallan: (id: string) => http.get<JobWorkChallan>(`${JWO_BASE}/${id}/challan`),
  getReturnChallan: (id: string) => http.get<JobWorkChallan>(`${JWO_BASE}/${id}/return-challan`),
  create: (payload: CreateJobWorkOrderPayload) =>
    http.post<JobWorkOrder, CreateJobWorkOrderPayload>(JWO_BASE, payload),
  approve: (id: string) => http.patch<JobWorkOrder, undefined>(`${JWO_BASE}/${id}/approve`),
  reject: (id: string, reviewerNote?: string) =>
    http.patch<JobWorkOrder, { reviewerNote?: string }>(`${JWO_BASE}/${id}/reject`, { reviewerNote }),
  cancel: (id: string) => http.patch<JobWorkOrder, undefined>(`${JWO_BASE}/${id}/cancel`),
  issue: (id: string, payload: JwoIssuePayload) =>
    http.patch<JobWorkOrder, JwoIssuePayload>(`${JWO_BASE}/${id}/issue`, payload),
  receive: (id: string, payload: JwoReceivePayload) =>
    http.patch<JobWorkOrder, JwoReceivePayload>(`${JWO_BASE}/${id}/receive`, payload),
};
